export interface UpdateSnapshot { phase: 'idle' | 'waiting' | 'ready' | 'applying'; online: boolean; checking: boolean; error: string }
interface UpdateActions { canReload: () => boolean; reload: () => void; apply: () => Promise<void>; check: () => Promise<void> }
export function createWarehouseUpdates(actions: UpdateActions, online = true) {
  let state: UpdateSnapshot = { phase: 'idle', online, checking: false, error: '' };
  const listeners = new Set<() => void>(); let requested = false; let timer: ReturnType<typeof setTimeout> | null = null;
  const set = (patch: Partial<UpdateSnapshot>) => { state = { ...state, ...patch }; listeners.forEach(fn => fn()); };
  function clearTimer() { if (timer) clearTimeout(timer); timer = null; }
  function stopReload() { requested = false; clearTimer(); }
  const canApply = () => state.online && actions.canReload();
  return {
    snapshot: () => state,
    subscribe: (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; },
    waiting: () => { if (state.phase !== 'applying') set({ phase: 'waiting', error: '' }); },
    activated: () => {
      const reload = requested && canApply(); stopReload(); set({ phase: 'ready', error: '' });
      if (reload) actions.reload();
    },
    network: (value: boolean) => { if (!value) { stopReload(); if (state.phase === 'applying') set({ phase: 'waiting' }); } set({ online: value }); },
    issue: () => { stopReload(); set({ error: 'The update check could not finish. Your current screen is still open.', ...(state.phase === 'applying' ? { phase: 'waiting' as const } : {}) }); },
    cancel: () => { stopReload(); if (state.phase === 'applying') set({ phase: 'waiting' }); },
    check: async () => {
      if (!state.online || state.checking || state.phase === 'applying') return;
      set({ checking: true, error: '' });
      try { await actions.check(); } catch { set({ error: 'The update check could not finish. Try again when connected.' }); }
      finally { set({ checking: false }); }
    },
    apply: async () => {
      if (!canApply() || !['waiting', 'ready'].includes(state.phase)) return;
      if (state.phase === 'ready') { actions.reload(); return; }
      requested = true; set({ phase: 'applying', error: '' });
      timer = setTimeout(() => { stopReload(); set({ phase: 'waiting', error: 'The update has not confirmed it is ready. Keep working or try again later.' }); }, 20000);
      try { await actions.apply(); }
      catch { stopReload(); set({ phase: 'waiting', error: 'The update could not finish. Your current screen is still open.' }); }
    },
    dispose: () => { stopReload(); listeners.clear(); },
  };
}
